import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {ObjectBaseComponent} from 'features/core/shared/object/mvs-object-base/object-base.component';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {ObserverService} from 'features/core/shared/object/service/observer.service';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {DtoList} from 'features/core/shared/dto/dto.list';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {ProvisionContractRunStatus} from '../../../enum/provision-contract-run-status.enum';
import {ProvisionContractRunDto} from '../../../model/dto/entity/provision-contract-run.dto';
import {CpRunReviewLookupService} from '../../../service/ql/cp-run-review-lookup.service';
import {CpRunContractRunView} from '../../../interface/cp-run-contract-run-view.interface';
import {CpRunMessageView} from '../../../interface/cp-run-message-view.interface';
import {
    getCpRunMessageGuidance,
    getCpRunSeverityLabel,
    getCpRunSeverityTag
} from '../../../service/domain/cp-run-message-guidance';
import {buildCpRunWidgetForm} from '../../../service/domain/cp-run-widget-column';
import {cpOpenContract} from '../../../service/domain/cp-object-navigation';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: ObjectBaseComponent
 * Reason: READ-ONLY detail of one contract in one run (`cp.ProvisionContractRun`) — right pane of
 *         the run-detail tab "Verträge". An ObjectBaseComponent on that entity row: the framework
 *         loads the `ProvisionContractRunDto` (run + contract ids, outcome), `onObjectChanged()`
 *         then enriches it read-only.
 *
 * Replaces the generic CRUD form that used to open here: it offered the run outcome as an editable
 * dropdown plus save and delete, and it did not say WHY a contract failed. This pane answers the
 * reviewer's three questions without a single write path:
 *   - Ergebnis: the outcome of the contract in this run (from the DTO),
 *   - Fehlergrund: every message of this contract run, severest first, each with "Was tun?"
 *     (QL view of the lookup service),
 *   - Betrag: the bookings this run produced for the contract — a `createWidgetTableQl` widget
 *     filtered by the DTO's run + contract — with their sum,
 * plus the contract (read-only jump to cm) and its Hauptvermittler with Vermittler-Id and
 * Abrechnungskonto.
 *
 * Host contract: the review workbench binds `[contractRunId]`; the setter turns it into the
 * standard `objectIdentifier`, so the component also works when created by the object framework.
 */
@Component({
    selector: 'cp-run-detail-contract-run',
    templateUrl: './cp-run-detail-contract-run.component.html',
    styleUrls: ['./cp-run-detail-contract-run.component.scss'],
    standalone: false
})
export class CpRunDetailContractRunComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    static readonly OBJECT_TYPE: string = 'cp.ProvisionContractRun';

    /** Id of the `cp.ProvisionContractRun` row — sets {@link objectIdentifier}. */
    @Input()
    set contractRunId(id: number) {
        this.objectIdentifier = id ? new ObjectIdentifier(CpRunDetailContractRunComponent.OBJECT_TYPE, id) : null;
    }

    get contractRunId(): number {
        return this.objectIdentifier?.objectId ?? null;
    }

    /** The loaded entity row, typed. */
    get contractRun(): ProvisionContractRunDto {
        return this.dto as ProvisionContractRunDto;
    }

    /** Messages, agents and booking total of the contract run (QL view). */
    view: CpRunContractRunView;
    viewBusy: boolean;
    loadFailed: boolean;

    /** Bookings of this run for this contract. */
    bookingsWidget: WidgetData;

    /** Count and sum of the bookings rows the widget loaded — the widget is the single loader. */
    bookingsLoaded: boolean;
    bookingsFailed: boolean;
    bookingCount: number = 0;
    bookingTotal: number = 0;

    private viewSubscription: Subscription;

    constructor(coreService: MvsCoreService,
                messageService: MvsMessageService,
                confirmationService: ConfirmationService,
                observerService: ObserverService,
                protected lookupService: CpRunReviewLookupService,
                protected navigationService: MvsObjectNavigationService) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    /** Drop a late answer for a row that is no longer selected (fast clicking in the worklist). */
    protected storeNewDto(dto: any): void {
        if (dto && this.objectIdentifier && dto.id != this.objectIdentifier.objectId) {
            return;
        }
        super.storeNewDto(dto);
    }

    /** The DTO is (re)loaded: rebuild the bookings widget and load the read-only view. */
    onObjectChanged(): void {
        this.loadFailed = false;
        this.refreshBookingsWidget();
        this.loadView();
    }

    /** The entity row could not be loaded — show the error state with a retry. */
    postRefreshObjectError(error: any): void {
        super.postRefreshObjectError(error);
        this.view = null;
        this.bookingsWidget = null;
        this.loadFailed = true;
    }

    /** Retry after an error: the entity row if it failed, otherwise only the view. */
    retry(): void {
        this.loadFailed = false;
        if (!this.objectIdentifier) {
            return;
        }
        if (this.dto) {
            this.loadView();
        } else {
            this.refreshObject(false);
        }
    }

    /** Load messages, Hauptvermittler and booking total of the contract run. */
    protected loadView(): void {

        this.viewSubscription?.unsubscribe();
        this.view = null;

        if (!this.objectIdentifier) {
            this.viewBusy = false;
            return;
        }

        this.viewBusy = true;

        this.viewSubscription = this.lookupService.loadContractRun(this.objectIdentifier.objectId).subscribe({
            next: view => {
                this.view = view;
                this.viewBusy = false;
            },
            error: () => {
                this.loadFailed = true;
                this.viewBusy = false;
            }
        });
    }

    /** Build the bookings widget from the run + contract of the DTO (always a fresh instance). */
    protected refreshBookingsWidget(): void {

        this.bookingsWidget = null;
        this.bookingsLoaded = false;
        this.bookingsFailed = false;
        this.bookingCount = 0;
        this.bookingTotal = 0;

        const contractRun = this.contractRun;

        if (!contractRun?.provisionRunDtoId || !contractRun?.contractDtoId) {
            return;
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Bookings (cp.ProvisionContractTransaction) that one provision run produced for one
         *         contract — Provisionsart, Berater, Valuta and Betrag, filtered by the run and the
         *         contract of the selected cp.ProvisionContractRun. Read-only.
         */
        this.bookingsWidget = WidgetFactory.createWidgetTableQl(
            'cp.run.detail.contractRun.bookings.table',
            'Buchungen dieses Laufs',
            this.buildBookingsQuery(contractRun.provisionRunDtoId, contractRun.contractDtoId),
            WidgetDataParam.create('size', 'S'),
            // One contract in one run books a handful of rows; the page must hold all of them
            // because the sum below is computed from the loaded rows.
            WidgetDataParam.create('paging', 200),
            WidgetDataParam.create('widgetForm', buildCpRunWidgetForm([
                {field: 'ptc.name', label: 'Provisionsart'},
                {field: 'ag.name', label: 'Berater'},
                {field: 'e.valutaDate', label: 'Valuta', outputControl: 'date'},
                {field: 'e.amount', label: 'Betrag', outputControl: 'currencyEuro'}
            ]))
        );
    }

    /** The bookings widget loaded its rows: take count and sum from them (no second QL call). */
    handleBookingsLoaded(dtoList: DtoList): void {
        if (!Array.isArray(dtoList?.entries)) {
            // A failed load emits without entries: an error, never "no bookings".
            this.bookingsFailed = true;
            this.bookingsLoaded = false;
            this.bookingCount = 0;
            this.bookingTotal = 0;
            return;
        }
        this.bookingsFailed = false;
        const entries = dtoList.entries as unknown as Record<string, unknown>[];
        this.bookingCount = entries.length;
        this.bookingTotal = entries.reduce((sum, entry) => sum + Number(entry?.['e.amount'] ?? 0), 0);
        this.bookingsLoaded = true;
    }

    /**
     * The bookings of one run for one contract. The contract is reached over the reconcile anchor
     * `provisionContractAgent` (inner join: a booking always has one), the agent over its account.
     */
    protected buildBookingsQuery(provisionRunId: number, contractId: number): QlQueryDto {
        return {
            name: 'cp.run.detail.contractRun.bookings',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractTransaction',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'provisionContractAgent', as: 'pca', joinType: 'inner',
                        fields: [{name: 'contractId'}],
                        joins: [
                            {type: 'entity', name: 'provisionTypeComponent', as: 'ptc', joinType: 'left', fields: [{name: 'name'}]},
                            {
                                type: 'entity', name: 'provisionAgent', as: 'pa', joinType: 'left',
                                joins: [{type: 'entity', name: 'agent', as: 'ag', joinType: 'left', fields: [{name: 'name'}]}]
                            }
                        ]
                    }
                ]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, provisionRunId),
                FilterCriteria.create('pca.contractId', FilterCriteria.cOperatorEqual, contractId)
            ],
            sortings: [new Sorting('e.valutaDate', true)]
        };
    }

    /** German label of the per-contract outcome. */
    getStatusLabel(status: number): string {
        switch (status) {
            case ProvisionContractRunStatus.CALCULATED:
                return 'Berechnet';
            case ProvisionContractRunStatus.UNCHANGED:
                return 'Unverändert';
            case ProvisionContractRunStatus.NOT_ELIGIBLE:
                return 'Nicht provisionsrelevant';
            case ProvisionContractRunStatus.CANCELLATION_PROCESSED:
                return 'Storno verarbeitet';
            case ProvisionContractRunStatus.ERROR:
                return 'Fehler';
            case ProvisionContractRunStatus.ABORTED:
                return 'Abgebrochen';
            default:
                return '–';
        }
    }

    /** PrimeNG tag severity of the per-contract outcome. */
    getStatusTag(status: number): 'success' | 'danger' | 'secondary' | 'info' | 'warn' {
        switch (status) {
            case ProvisionContractRunStatus.CALCULATED:
                return 'success';
            case ProvisionContractRunStatus.ERROR:
                return 'danger';
            case ProvisionContractRunStatus.ABORTED:
                return 'warn';
            case ProvisionContractRunStatus.CANCELLATION_PROCESSED:
                return 'info';
            default:
                return 'secondary';
        }
    }

    /** German label of a message severity. */
    getSeverityLabel(severity: number): string {
        return getCpRunSeverityLabel(severity);
    }

    /** PrimeNG tag severity of a message severity. */
    getSeverityTag(severity: number): 'danger' | 'warn' | 'info' {
        return getCpRunSeverityTag(severity);
    }

    /** "Was tun?" text of a message code. */
    getGuidance(message: CpRunMessageView): string {
        return getCpRunMessageGuidance(message.messageCode).action;
    }

    /** Open the cm contract page, read-only. */
    openContract(): void {
        cpOpenContract(this.navigationService, this.contractRun?.contractDtoId ?? this.view?.contractId);
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        // The host binds `contractRunId`, not `objectIdentifier` — the base class does not see it.
        const idChange = changes['contractRunId'];
        if (!idChange || idChange.firstChange) {
            return;
        }

        this.viewSubscription?.unsubscribe();
        this.view = null;
        this.bookingsWidget = null;
        this.bookingsLoaded = false;
        this.bookingsFailed = false;
        this.loadFailed = false;

        if (this.objectIdentifier) {
            this.refreshObject(false);
        } else {
            this.dto = null;
            this.initialized = false;
            this.busy = false;
            this.viewBusy = false;
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.viewSubscription?.unsubscribe();
    }
}
